import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography, List, ListItem, Link, Paper } from '@mui/material';
import mermaid from 'mermaid';

// Initialize mermaid — 'loose' is required for labels to render inside nodes
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
  },
});

// Inline component to render a single Mermaid diagram
const MermaidBlock = ({ code }) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const renderDiagram = async () => {
      try {
        let cleanCode = (code || '').trim();
        cleanCode = cleanCode.replace(/^```mermaid\s*/i, '').replace(/```\s*$/, '').trim();
        if (!cleanCode) return;

        const id = `mermaid-${Math.random().toString(36).substring(2, 10)}`;
        const { svg: renderedSvg } = await mermaid.render(id, cleanCode);
        if (!cancelled) {
          // Mermaid's own securityLevel handles sanitization.
          // DOMPurify strips foreignObject elements that Mermaid uses for HTML labels,
          // which causes empty nodes. Since this is Mermaid-generated (not user input),
          // we trust the output directly.
          setSvg(renderedSvg);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Mermaid render error:', err);
          setError(err.message || 'Failed to render diagram');
        }
      }
    };
    renderDiagram();
    return () => { cancelled = true; };
  }, [code]);

  if (error) {
    return (
      <Box sx={{ my: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
        <Typography variant="caption" color="warning.main">Diagram render error: {error}</Typography>
        <Box component="pre" sx={{ mt: 1, fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {code}
        </Box>
      </Box>
    );
  }

  if (!svg) {
    return (
      <Box sx={{ my: 2, p: 2, textAlign: 'center', color: 'text.secondary' }}>
        Rendering diagram...
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        my: 2,
        p: 2,
        bgcolor: '#fafafa',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        overflowX: 'auto',
        overflowY: 'visible',
        // Let the diagram expand fully — no height constraints
        '& svg': {
          width: '100%',
          minHeight: 200,
          height: 'auto !important',
          maxHeight: 'none !important',
        },
        // Ensure text labels are visible
        '& .nodeLabel, & .edgeLabel, & text, & .label': {
          fontSize: '14px !important',
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
          fill: '#333 !important',
        },
        '& .node rect, & .node polygon, & .node circle, & .node ellipse': {
          stroke: '#6e7681 !important',
        },
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

// Process Unicode escape sequences in content
export const processUnicodeContent = (content) => {
  if (!content) return '';
  
  // Process \uXXXX Unicode escape sequences (BMP characters)
  const bmpProcessed = content.replace(/\\u([0-9a-fA-F]{4})/g, (_, codePoint) => {
    return String.fromCharCode(parseInt(codePoint, 16));
  });
  
  // Process \uXXXXXXXX Unicode escape sequences (supplementary plane characters)
  return bmpProcessed.replace(/\\u([0-9a-fA-F]{8})/g, (_, codePoint) => {
    const highSurrogate = parseInt(codePoint.substring(0, 4), 16);
    const lowSurrogate = parseInt(codePoint.substring(4, 8), 16);
    return String.fromCharCode(highSurrogate, lowSurrogate);
  });
};

const MarkdownRenderer = ({ content, className }) => {
  // Process Unicode escape sequences in the content
  let processedContent = processUnicodeContent(content);
  
  // Fix formatting issues where section headers don't start on new lines
  if (processedContent && processedContent.includes('**') && processedContent.includes(':**')) {
    // Debug log before fixing
    // console.log('MarkdownRenderer - Before format fix:', processedContent.substring(0, 500));
    
    // Replace any occurrence where text is followed immediately by a bold section header
    // This regex matches any character that's not a newline, followed by ** and ending with :**
    processedContent = processedContent.replace(/([^\n])(\*\*[^*]+:\*\*)/g, '$1\n\n$2');
    
    // Also fix cases where there's only one newline before a section header
    processedContent = processedContent.replace(/([^\n])\n(\*\*[^*]+:\*\*)/g, '$1\n\n$2');
    
    // Fix specific pattern like "fluctuates. **Relationship to other roles:**"
    processedContent = processedContent.replace(/([.!?:;])\s*(\*\*[^*]+:\*\*)/g, '$1\n\n$2');
    
    // Clean up any excessive newlines (more than 2)
    processedContent = processedContent.replace(/\n{3,}/g, '\n\n');
    
    // Debug log after fixing
    // console.log('MarkdownRenderer - After format fix:', processedContent.substring(0, 500));
  }
  
  return (
    <Box className={className} sx={{ width: '100%' }}>
      <Paper elevation={0} sx={{ p: 2, backgroundColor: 'transparent' }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({ node, inline, className, children, ...props }) {
              const langMatch = /language-(\w+)/.exec(className || '');
              const language = langMatch ? langMatch[1] : '';

              if (inline) {
                return (
                  <Box
                    component="code"
                    sx={{
                      backgroundColor: '#f5f5f5',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }}
                    {...props}
                  >
                    {children}
                  </Box>
                );
              }

              // Render Mermaid diagrams visually
              if (language === 'mermaid') {
                const mermaidCode = String(children).replace(/\n$/, '');
                return <MermaidBlock code={mermaidCode} />;
              }

              return (
                <Box sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: '4px',
                  my: 2,
                  overflow: 'auto'
                }}>
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0
                    }}
                    {...props}
                  >
                    {children}
                  </Box>
                </Box>
              );
            },
            h1: ({ node, ...props }) => <Typography variant="h4" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }} {...props} />,
            h2: ({ node, ...props }) => <Typography variant="h5" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }} {...props} />,
            h3: ({ node, ...props }) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }} {...props} />,
            h4: ({ node, ...props }) => <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }} {...props} />,
            h5: ({ node, ...props }) => <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }} {...props} />,
            h6: ({ node, ...props }) => <Typography variant="body2" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }} {...props} />,
            p: ({ node, ...props }) => <Typography variant="body1" sx={{ my: 1 }} {...props} />,
            a: ({ node, ...props }) => <Link color="primary" {...props} />,
            ul: ({ node, ...props }) => <List sx={{ listStyleType: 'disc', pl: 4, '& .MuiListItem-root': { display: 'list-item' } }} {...props} />,
            ol: ({ node, ...props }) => <List sx={{ listStyleType: 'decimal', pl: 4, '& .MuiListItem-root': { display: 'list-item' } }} {...props} />,
            li: ({ node, ...props }) => <ListItem sx={{ py: 0.25 }} {...props} />,
            blockquote: ({ node, ...props }) => (
              <Box sx={{ 
                borderLeft: '4px solid #ddd', 
                pl: 2, 
                py: 0.5, 
                my: 2,
                fontStyle: 'italic',
                color: 'text.secondary'
              }} {...props} />
            ),
            img: ({ node, ...props }) => (
              <Box 
                component="img" 
                sx={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  borderRadius: '4px',
                  my: 2 
                }} 
                {...props} 
                alt={props.alt || ""} 
              />
            ),
            table: ({ node, ...props }) => (
              <Box sx={{ overflowX: 'auto', my: 2 }}>
                <Box 
                  component="table" 
                  sx={{ 
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.875rem'
                  }} 
                  {...props} 
                />
              </Box>
            ),
            th: ({ node, ...props }) => (
              <Box
                component="th"
                sx={{
                  border: '1px solid #ddd',
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  textAlign: 'left',
                  fontWeight: 'bold'
                }}
                {...props}
              />
            ),
            td: ({ node, ...props }) => (
              <Box
                component="td"
                sx={{
                  border: '1px solid #ddd',
                  padding: '8px 12px'
                }}
                {...props}
              />
            ),
            tr: ({ node, ...props }) => (
              <Box
                component="tr"
                sx={{
                  '&:nth-of-type(even)': {
                    backgroundColor: '#f9f9f9'
                  }
                }}
                {...props}
              />
            ),
            hr: ({ node, ...props }) => (
              <Box
                component="hr"
                sx={{
                  border: 'none',
                  height: '1px',
                  backgroundColor: '#ddd',
                  my: 2
                }}
                {...props}
              />
            ),
            strong: ({ node, ...props }) => (
              <Box component="strong" sx={{ fontWeight: 'bold' }} {...props} />
            ),
            em: ({ node, ...props }) => (
              <Box component="em" sx={{ fontStyle: 'italic' }} {...props} />
            ),
            div: ({ node, className, ...props }) => {
              if (className === 'numbered-item') {
                return (
                  <Box sx={{ display: 'flex', mb: 1 }} {...props} />
                );
              }
              return <Box {...props} />;
            }
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </Paper>
    </Box>
  );
};

export default MarkdownRenderer;